import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap, filter, take, tap, timeout } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { paths } from 'src/environments/paths';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth.service';
import { NetworkService } from '../service/network.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private defaultApiTimeout = 15000;

  constructor(private http: HttpClient, 
    private router: Router,
    private authService: AuthService,
    private networkService: NetworkService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headers = request.headers;
    headers = headers.set('registeredUserId', this.authService.getUserId() || '');
    // Ensure credentials (cookies) are sent with every request
    const clonedRequest = request.clone({ withCredentials: true, headers });
    let apiObservable = next.handle(clonedRequest)
    apiObservable = apiObservable.pipe(
        timeout(this.defaultApiTimeout)
    );
    return apiObservable.pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          console.log('AuthInterceptor: Request successful:', event);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0 && error.statusText === 'Unknown Error') {
          console.error('AuthInterceptor: Network error detected. No internet connection.');
          this.networkService.setNetworkStatus(false);
          return EMPTY;
        }
        console.error('AuthInterceptor: Caught error for', error.url, 'Status:', error.status);
        if ((error.status === 401 || error.status === 403) && !error.error.metaData) {
            console.log('AuthInterceptor: Detected auth error:', error);
            if (request.url.includes('/auth/refresh')) {
                console.error('Refresh token failed. User must re-login.');
                this.forceLogout();
                return throwError(() => error);
            }

            if (!this.isRefreshing) {
                this.isRefreshing = true;
                this.refreshTokenSubject.next(null); // Clear previous token, indicate refresh in progress

                console.log('Session token expired/invalid. Attempting to refresh tokens...');

                // Make the call to your refresh endpoint
                return this.http.post(paths.AUTH_REFRESH, {}, { withCredentials: true }).pipe(
                    switchMap((response: any) => {
                        this.isRefreshing = false;
                        this.refreshTokenSubject.next(response); // Notify that refresh succeeded
                        // Retry the original failed request with new cookies
                        return next.handle(request.clone({ withCredentials: true }));
                    }),
                    catchError((refreshError: HttpErrorResponse) => {
                        this.isRefreshing = false;
                        // Refresh token failed, force logout
                        console.error('Failed to refresh tokens:', refreshError);
                        this.forceLogout();
                        return throwError(() => refreshError);
                    })
                );
            } else {
                // If a refresh is already in progress, wait for it to complete
                // and then retry the original request with the new tokens
                return this.refreshTokenSubject.pipe(
                    filter(response => response !== null), // Wait until refresh is done
                    take(1),
                    switchMap(response => {
                        return next.handle(request.clone({ withCredentials: true }));
                    })
                );
            }
        }
        return throwError(() => error);
      })
    );
  }

  private forceLogout(): void {
    this.router.navigate(['/auth-error']);
  }
}